import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ServicePackageOrder } from 'src/app/models/service-package-order.model';
import { PackageService } from 'src/app/models/package-service.model';
import { SelectorBoatDTO } from 'src/app/models/dtos/selector-boat-DTO';
import { PackageServiceService } from 'src/app/services/package-service.service';
import { BoatService } from 'src/app/services/boat.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ToastService } from 'src/app/services/toast.service';
import { SelectItem } from 'primeng/api';
import { CurrencyUtil } from 'src/app/utils/currency.util';
import { Currency } from 'src/app/models/enums/currency';
import { Customer } from 'src/app/models/customer';

@Component({
  selector: 'app-service-package-order-form',
  templateUrl: './service-package-order-form.component.html',
  styleUrls: ['./service-package-order-form.component.scss']
})
export class ServicePackageOrderFormComponent implements OnInit {

  servicePackageOrderForm: FormGroup;
  servicePackageOrder: ServicePackageOrder;
  isEdit: boolean = false;
  packageServices: PackageService[] = [];
  boats: SelectorBoatDTO[] = [];
  packageServiceOptions: SelectItem[] = [];
  boatOptions: SelectItem[] = [];
  customers: Customer[] = [];
  customerOptions: SelectItem[] = [];
  loading: boolean = false;
  currentCurrency: Currency = Currency.BRL;

  constructor(
    private fb: FormBuilder,
    private packageServiceService: PackageServiceService,
    private boatService: BoatService,
    private customerService: CustomerService,
    private toastService: ToastService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.servicePackageOrder = config.data?.servicePackageOrder || new ServicePackageOrder();
    this.isEdit = config.data?.isEdit || false;
  }

  ngOnInit(): void {
    this.initForm();
    this.loadPackageServices();
    this.loadBoats();
  }

  initForm(): void {
    const today = new Date();

    this.servicePackageOrderForm = this.fb.group({
      packageService: [this.servicePackageOrder.packageService, [Validators.required]],
      boat: [this.servicePackageOrder.boat, [Validators.required]],
      customer: [null, [Validators.required]],
      validityStartDate: [today, [Validators.required]],
      validityEndDate: [this.calculateEndDate(today), [Validators.required]],
      value: [this.servicePackageOrder.value || 0, [Validators.min(0)]],
      discount: [this.servicePackageOrder.discount, [Validators.min(0)]],
      observations: [this.servicePackageOrder.observations]
    });

    this.servicePackageOrderForm.get('packageService')?.valueChanges.subscribe((packageService) => {
      if (packageService && packageService.daysDuration) {
        const startDate = this.servicePackageOrderForm.get('validityStartDate')?.value;
        if (startDate) {
          const endDate = this.calculateEndDate(startDate, packageService.daysDuration);
          this.servicePackageOrderForm.patchValue({ validityEndDate: endDate });
        }
      }
    });

    this.servicePackageOrderForm.get('validityStartDate')?.valueChanges.subscribe((startDate) => {
      if (startDate) {
        const packageService = this.servicePackageOrderForm.get('packageService')?.value;
        const daysDuration = packageService?.daysDuration || 30;
        const endDate = this.calculateEndDate(startDate, daysDuration);
        this.servicePackageOrderForm.patchValue({ validityEndDate: endDate });
      }
    });

    // Escuta mudanças na embarcação para carregar os clientes
    this.servicePackageOrderForm.get('boat')?.valueChanges.subscribe((boat) => {
      if (boat) {
        this.loadCustomersByBoat(boat.id);
      } else {
        this.customers = [];
        this.customerOptions = [];
        this.servicePackageOrderForm.patchValue({ customer: null });
      }
    });
  }

  loadPackageServices(): void {
    this.packageServiceService.findAll().subscribe({
      next: (data) => {
        this.packageServices = data.filter(ps => ps.active);
        this.packageServiceOptions = this.packageServices.map(ps => ({
          label: `${ps.name} (${ps.daysDuration} dias)`,
          value: ps
        }));
      },
      error: (error) => {
        console.error('Erro ao carregar pacotes de serviço:', error);
        this.toastService.error('Erro ao carregar pacotes de serviço');
      }
    });
  }

  loadBoats(): void {
    this.boatService.getAllForSelector().subscribe({
      next: (data) => {
        this.boats = data.filter(boat => boat.active);
        this.boatOptions = this.boats.map(boat => ({
          label: boat.name,
          value: boat
        }));
      },
      error: (error) => {
        console.error('Erro ao carregar embarcações:', error);
        this.toastService.error('Erro ao carregar embarcações');
      }
    });
  }

  loadCustomersByBoat(boatId: number): void {
    this.customerService.getByboatId(boatId).subscribe({
      next: (data) => {
        // Filtra apenas os proprietários (type === 'Owner')
        const owners = data.filter((item: any) => item.type === 'Owner').map((item: any) => item.customer);
        this.customers = owners;
        this.customerOptions = owners.map(customer => ({
          label: `${customer.name} - ${customer.federalId}`,
          value: customer
        }));
      },
      error: (error) => {
        console.error('Erro ao carregar clientes da embarcação:', error);
        this.toastService.error('Erro ao carregar clientes da embarcação');
        this.customers = [];
        this.customerOptions = [];
      }
    });
  }

  calculateEndDate(startDate: Date, daysDuration?: number): Date {
    const duration = daysDuration || 30;
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + duration);
    return endDate;
  }

  getPackageValue(): string {
    const packageService = this.servicePackageOrderForm.get('packageService')?.value;
    const boat = this.servicePackageOrderForm.get('boat')?.value;

    if (!packageService || !packageService.product) {
      return '';
    }

    const product = packageService.product;


    if (product.pricingType === 'SimplePrice' && product.price?.value) {
      return CurrencyUtil.formatCurrency(product.price.value, this.currentCurrency);
    }

    if ((product.pricingType === 'FixedPerRange' || product.pricingType === 'PerRangeLength') && product.price?.priceRanges) {
      if (!boat || !boat.commercialLength) {
        return '';
      }

      const boatLength = boat.commercialLength;
      const priceRange = product.price.priceRanges.find(range =>
        boatLength >= range.startLengthRange && boatLength <= range.endLengthRange
      );

      if (!priceRange) {
        return 'Tamanho fora da faixa';
      }

      if (product.pricingType === 'FixedPerRange') {
        return CurrencyUtil.formatCurrency(priceRange.value, this.currentCurrency);
      } else if (product.pricingType === 'PerRangeLength') {
        const calculatedValue = boatLength * priceRange.value;
        return CurrencyUtil.formatCurrency(calculatedValue, this.currentCurrency) +
               ' (' + boatLength + ' pés × ' + CurrencyUtil.formatCurrency(priceRange.value, this.currentCurrency) + ')';
      }
    }

    return '';
  }

  isValueEditable(): boolean {
    const packageService = this.servicePackageOrderForm.get('packageService')?.value;
    return packageService && packageService.product && packageService.product.price?.priceFree;
  }

  calculatePackageValue(packageService: any, boat: any): number {
    if (!packageService || !packageService.product) {
      return 0;
    }

    const product = packageService.product;

    if (product.price?.priceFree) {
      return 0;
    }

    if (product.pricingType === 'SimplePrice' && product.price?.value) {
      return product.price.value;
    }

    if ((product.pricingType === 'FixedPerRange' || product.pricingType === 'PerRangeLength') && product.price?.priceRanges) {
      if (!boat || !boat.commercialLength) {
        return 0;
      }

      const boatLength = boat.commercialLength;
      const priceRange = product.price.priceRanges.find(range =>
        boatLength >= range.startLengthRange && boatLength <= range.endLengthRange
      );

      if (!priceRange) {
        return 0;
      }

      if (product.pricingType === 'FixedPerRange') {
        return priceRange.value;
      } else if (product.pricingType === 'PerRangeLength') {
        return boatLength * priceRange.value;
      }
    }

    return 0;
  }

  save(): void {
    if (this.servicePackageOrderForm.valid) {
      this.loading = true;
      const formValue = this.servicePackageOrderForm.value;

      const payload = {
        packageServiceId: formValue.packageService.id,
        boatId: formValue.boat.id,
        customerId: formValue.customer.id,
        validityStartDate: formValue.validityStartDate,
        validityEndDate: formValue.validityEndDate,
        value: this.isValueEditable() ? formValue.value : this.calculatePackageValue(formValue.packageService, formValue.boat),
        discount: formValue.discount || 0,
        observations: formValue.observations || ''
      };

      this.packageServiceService.addPackageInBoat(payload).subscribe({
        next: (data) => {
          this.toastService.success('Pacote de serviço adicionado à embarcação com sucesso');
          this.loading = false;
          this.ref.close(data);
        },
        error: (error) => {
          this.toastService.error('Erro ao adicionar pacote de serviço à embarcação');
          this.loading = false;
        }
      });
    } else {
      this.toastService.warning('Por favor, preencha todos os campos obrigatórios');
    }
  }

  cancel(): void {
    this.ref.close();
  }
}
